/**
 * Koji DE — dodanie pakietu do koszyka (multi-line) + otwarcie cart drawera.
 *
 * Deal-bary (koji-packages.liquid) trzymają wybór pakietu + kolorów w DOM.
 * Ten moduł po kliknięciu przycisku zbiera wybrane warianty, dodaje je jako
 * osobne pozycje z właściwością `_bundle` (czyta ją discount function)
 * i przez event Horizona `CartLinesUpdateEvent` odświeża i otwiera cart drawer
 * — tak samo jak natywny add-to-cart.
 *
 * Mapa kolor → wariant pochodzi z `data-koji-variants` (tytuły wariantów sklepu:
 * na DE Schwarz / Blau / Rosa) i jest porównywana z `data-color` swatchy.
 * Kod nie zawiera nazw kolorów na sztywno.
 *
 * Fallback: przy jakimkolwiek błędzie przekierowuje na /cart (stare zachowanie).
 */

import { CartLinesUpdateEvent } from '@shopify/events';

function routeBase() {
  const r =
    window.Shopify && Shopify.routes && Shopify.routes.root
      ? Shopify.routes.root
      : '/';
  return r.replace(/\/$/, '');
}

function collectItems(root) {
  const deals = root.querySelector('[data-koji-deals]');
  if (!deals) return null;
  const active = deals.querySelector('.deal.is-active');
  if (!active) return null;

  let variants = {};
  const vEl = root.querySelector('[data-koji-variants]');
  if (vEl) {
    try {
      variants = JSON.parse(vEl.textContent);
    } catch (e) {
      /* noop */
    }
  }

  const bundleKey = active.getAttribute('data-bundle') || '';
  const bundleName = active.getAttribute('data-bundle-name') || '';

  // agregacja tych samych kolorów w quantity
  const rows = active.querySelectorAll('.color-row');
  const counts = {};
  rows.forEach((rowEl) => {
    const sel =
      rowEl.querySelector('.sw.is-active') || rowEl.querySelector('.sw');
    if (!sel) return;
    const id = variants[sel.getAttribute('data-color')];
    if (!id) return;
    counts[id] = (counts[id] || 0) + 1;
  });

  const items = Object.keys(counts).map((id) => {
    const properties = {};
    if (bundleKey) {
      properties._bundle = bundleKey;
      // Nazwa pakietu i OBIECANA liczba sztuk idą jako UKRYTE właściwości
      // (podkreślnik). Widoczną właściwość Shopify wypisuje w koszyku,
      // w potwierdzeniu zamówienia i w mailu — a podpis zestawu
      // przeżywa zmniejszenie ilości w koszyku: rabat (próg ilościowy)
      // przestaje działać, cena wraca do ceny za sztukę, a podpis zostaje
      // nad jednym ręcznikiem. Klientka czyta podpis, nie cenę
      // (CZ: reklamacja #1842, 14 zamówień łącznie).
      // Podpis renderuje snippets/cart-products.liquid — i tylko wtedy,
      // gdy zestaw jest kompletny.
      if (bundleName) properties._bundle_name = bundleName;
      properties._bundle_units = String(rows.length);
    }
    return { id: Number(id), quantity: counts[id], properties };
  });

  // Shopify pokazuje pozycje z jednego /cart/add.js w odwrotnej kolejności
  // (sprawdzone 29. 9. na SK) — dlatego ręczniki od tyłu: w koszyku kolory
  // idą jak na kafelku. (Gratis z D-102 zdjęty po DE-002; dawny kod: commit 12a3a80.)
  items.reverse();

  return items.length ? items : null;
}

async function addBundle(root, btn) {
  const items = collectItems(root);
  if (!items) return;

  const base = routeBase();

  btn.setAttribute('disabled', 'disabled');
  btn.classList.add('is-loading');

  // sekcje do re-renderu (wiersze koszyka / drawer)
  const sectionIds = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el.dataset && el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
  });
  const sections = Array.from(sectionIds).join(',');

  const totalQty = items.reduce((sum, it) => sum + it.quantity, 0);
  const deferred = CartLinesUpdateEvent.createPromise();

  // informujemy motyw, że trwa dodawanie (drawer z auto-open otworzy się na 'add')
  root.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((it) => ({
        merchandiseId: 'gid://shopify/ProductVariant/' + it.id,
        quantity: it.quantity,
      })),
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(base + '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items,
        sections,
        sections_url: window.location.pathname,
      }),
    });
    if (!res.ok) throw new Error('add failed');
    const data = await res.json();

    let cart = null;
    try {
      const cres = await fetch(base + '/cart.js', {
        headers: { Accept: 'application/json' },
      });
      cart = CartLinesUpdateEvent.createCartFromAjaxResponse(await cres.json());
    } catch (e) {
      /* count fallback */
    }

    deferred.resolve({
      cart,
      detail: {
        sections: data.sections,
        items: data.items,
        source: 'koji-bundle',
        itemCount: totalQty,
      },
    });
  } catch (e) {
    if (typeof deferred.reject === 'function') deferred.reject(e);
    window.location.href = base + '/cart';
    return;
  } finally {
    btn.removeAttribute('disabled');
    btn.classList.remove('is-loading');
  }
}

function init() {
  document.querySelectorAll('.koji-packages').forEach((root) => {
    if (root.dataset.kojiAddInit) return;
    root.dataset.kojiAddInit = '1';
    const btn = root.querySelector('[data-koji-add]');
    if (!btn) return;
    btn.addEventListener('click', () => {
      addBundle(root, btn);
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
